import { createContext, FC, ReactNode, useMemo, useState } from "react";
import { WizardStep as ApiWizardStep } from "../types/wizard";
import {
  Alternatives,
  Parameter,
  ParameterType,
  ParameterValue,
} from "../types/parameters";
import { debounce } from "@mui/material";
import { getWizardSteps, getWizardStepsByInput } from "../../api/wizard-api";
import {
  getDisplayValue,
  getType,
  parseAsSelectionAlternative,
} from "../../components/data-presentation/util";
import useAPIError from "../hooks/useAPIError";

export type WizardContextType = ReturnType<typeof useWizard>;

export const WizardContext = createContext<WizardContextType | null>(null);

interface WizardProviderProps {
  customer: string;
  economicProfile: string;
  children?: ReactNode;
}

export const WizardContextProvider: FC<WizardProviderProps> = ({
  customer,
  economicProfile,
  children,
}) => {
  const contextValue = useWizard(customer, economicProfile);

  return (
    <WizardContext.Provider value={contextValue}>
      {children}
    </WizardContext.Provider>
  );
};

interface Profile {
  customer?: string;
  economicProfile?: string;
}

interface WizardState {
  meta: Profile;
  currentStep?: string;
  steps: WizardStepState[];
}

interface WizardStepValue {
  rawParameterValues: ParameterValue[];
  display: string;
}

interface WizardStepState {
  id: string;
  value?: WizardStepValue;
  defaultValue?: WizardStepValue;
  completed: boolean;
}

export interface WizardStep extends ApiWizardStep {
  isCompleted: boolean;
  displayValue: string | undefined;
}

const STORAGE_KEY = "wizardStepsState";

function loadOrCreateState(
  customer: string,
  economicProfile: string
): WizardState {
  const storedValue = sessionStorage.getItem(STORAGE_KEY);
  if (storedValue !== null) {
    let state = JSON.parse(storedValue as string) as WizardState;
    if (
      state.meta.customer === customer &&
      state.meta.economicProfile === economicProfile
    ) {
      return state;
    }
  }
  return createState(customer, economicProfile);
}

function createState(customer: string, economicProfile: string): WizardState {
  let state: WizardState = { meta: { customer, economicProfile }, steps: [] };
  storeState(state);
  return state;
}

function storeState(state: WizardState) {
  sessionStorage.setItem("wizardStepsState", JSON.stringify(state));
}

function getOrCreateStepState(
  state: WizardState,
  stepId: string
): WizardStepState {
  let stepState = getStateForStepWithId(stepId, state);
  if (stepState === undefined) {
    stepState = stepState = {
      id: stepId,
      value: undefined,
      completed: false,
    };
    state.steps.push(stepState);
  }
  return stepState;
}

function getStateForStepWithId(
  id: string,
  wizardState: WizardState
): WizardStepState | undefined {
  return wizardState.steps.find((stepState) => stepState.id === id);
}

function storeDefaultValues(apiSteps: ApiWizardStep[], state: WizardState) {
  for (let apiStep of apiSteps) {
    let defaultRawStepValues = getRawDefaultValues(apiStep);
    let defaultDisplayValue = getWizardStepDisplayValue(
      defaultRawStepValues,
      apiStep.inputParameters
    );

    let storedStep = getOrCreateStepState(state, apiStep.id);

    let hasMadeChangesToDefaultValue = false;
    let hasMadeChangesToUserInput = false;
    for (let newDefaultValue of defaultRawStepValues) {
      let storedDefaultValue =
        storedStep?.defaultValue?.rawParameterValues?.find(
          (defaultValue) => defaultValue.id === newDefaultValue.id
        );
      let storedUserInputValue = storedStep?.value?.rawParameterValues?.find(
        (input) => input.id === newDefaultValue.id
      );
      if (
        // Only update store if a default value hasn't been added earlier,
        // or if the step has not been completed,
        // or if the user has used the default value
        hasValue(newDefaultValue.value) &&
        storedDefaultValue?.value !== newDefaultValue.value &&
        (storedDefaultValue === undefined ||
          !hasValue(storedUserInputValue?.value) ||
          storedUserInputValue?.value === storedDefaultValue.value)
      ) {
        // If the user has used the default value, update it as well
        if (
          hasValue(storedUserInputValue?.value) &&
          storedUserInputValue?.value === storedDefaultValue?.value
        ) {
          storedUserInputValue!.value = newDefaultValue.value;
          hasMadeChangesToUserInput = true;
        }

        if (storedDefaultValue !== undefined) {
          storedDefaultValue.value = newDefaultValue.value;
        } else {
          if (storedStep.defaultValue === undefined) {
            storedStep.defaultValue = {
              rawParameterValues: [],
              display: defaultDisplayValue ?? newDefaultValue.value ?? "",
            };
          }
          storedStep.defaultValue.rawParameterValues.push({
            ...newDefaultValue,
          });
        }

        hasMadeChangesToDefaultValue = true;
      }
    }

    if (hasMadeChangesToDefaultValue || hasMadeChangesToUserInput) {
      let newDisplayValue =
        defaultDisplayValue ??
        storedStep.defaultValue?.rawParameterValues.at(0)?.value ??
        "";
      if (hasMadeChangesToDefaultValue) {
        storedStep.defaultValue!.display = newDisplayValue;
      }

      if (hasMadeChangesToUserInput) {
        storedStep.value!.display = newDisplayValue;
      }
    }

    storeState(state);
  }
}

function getWizardStepDisplayValue(
  values: ParameterValue[],
  parameters: Parameter[]
): string | undefined {
  if (values.length === 0 || parameters.length === 0) {
    return undefined;
  }

  let rawValue = values[0];
  if (rawValue.value === undefined) {
    return undefined;
  }

  let stringValue =
    rawValue.value === undefined
      ? undefined
      : typeof rawValue.value === "number"
        ? JSON.stringify(rawValue.value)
        : rawValue.value;
  let matchingParameter = parameters.find(
    (parameter) => parameter.id === rawValue.id
  );
  if (matchingParameter === undefined) {
    return undefined;
  }

  return getDisplayValue(
    stringValue,
    false,
    getType(matchingParameter),
    matchingParameter.stylingConfig,
    true
  );
}

function getRawDefaultValues(apiStep: ApiWizardStep): ParameterValue[] {
  let defaultValue: ParameterValue[] = apiStep.inputParameters;
  if (
    apiStep.inputParameters.every(
      (parameter) => getType(parameter) === ParameterType.Selection
    )
  ) {
    defaultValue = []; // We do not wan't to preselect a selection alternative even if there is a default value in the configuration, so that the user has free choice and "Beräkningarna bygger på manuellt justerade värden" will not appear if the user selects something else than the configured default value
  }
  defaultValue = defaultValue.map((parameter) => ({
    id: parameter.id,
    value:
      typeof parameter.value === "string"
        ? parameter.value
        : JSON.stringify(parameter.value),
  }));

  return defaultValue;
}

function mergeApiStepsWithState(
  apiSteps: ApiWizardStep[],
  state: WizardState,
  resyncSelectionAlternativeValues: boolean
): WizardStep[] {
  let mergedSteps: WizardStep[] = [];

  for (let apiWizardStep of apiSteps) {
    let stepState = getStateForStepWithId(apiWizardStep.id, state);
    let mergedStep: WizardStep = {
      ...apiWizardStep,
      inputParameters: [],
      isCompleted: stepState?.completed ?? false,
      displayValue:
        stepState?.value?.display ?? stepState?.defaultValue?.display,
    };
    mergedSteps.push(mergedStep);

    let userInput = stepState?.value?.rawParameterValues;
    let parameterValues =
      userInput ?? getDefaultValue(apiWizardStep, stepState);

    for (let apiParameter of apiWizardStep.inputParameters) {
      let mergedParameter: Parameter = {
        ...apiParameter,
        value: parameterValues?.find((input) => input.id === apiParameter.id)
          ?.value,
      };
      mergedStep.inputParameters.push(mergedParameter);

      // If the parmeter has non-editable alternatives and the profile (district, or building type) has changed,
      // the value of each alternative might differ in the new profile compared to the old. Since we store the
      // selected alternative along with its value, the value of the alternative might therefore be incorrect for the newly
      // selected profile. We thus switch to the alternative from the API output that shares the same name as the selected one.
      if (
        mergedParameter.value !== undefined &&
        resyncSelectionAlternativeValues &&
        getType(mergedParameter) === ParameterType.Selection
      ) {
        overwriteValueWithCorrespondingSelectionAlternative(
          mergedParameter,
          state
        );
      }
    }
  }

  return mergedSteps;
}

function overwriteValueWithCorrespondingSelectionAlternative(
  mergedParameter: Parameter,
  state: WizardState
) {
  let selectedAlternative = parseAsSelectionAlternative(mergedParameter.value);
  let matchingConstraint = (mergedParameter.constraints as Alternatives).find(
    (alternative) => alternative.id === selectedAlternative?.id
  );

  // If the new constraint differs from that of the old profile,
  // and the user is not allowed to edit the value
  if (
    selectedAlternative !== undefined &&
    matchingConstraint !== undefined &&
    selectedAlternative.value !== matchingConstraint.value &&
    !matchingConstraint.editable
  ) {
    mergedParameter.value = JSON.stringify(matchingConstraint);
    updateStoredUserInputForParameter(
      { id: mergedParameter.id, value: mergedParameter.value },
      matchingConstraint.displayName,
      state
    );
  }
}

function getDefaultValue(
  step: ApiWizardStep,
  state: WizardStepState | undefined
): ParameterValue[] | undefined {
  if (step.inputParameters.length !== 1) {
    return undefined;
  } else if (
    getType(step.inputParameters[0]) === ParameterType.Selection &&
    (step.inputParameters[0].constraints as Alternatives)?.length === 1
  ) {
    return [
      {
        id: step.inputParameters[0].id,
        value: JSON.stringify(
          (step.inputParameters[0].constraints as Alternatives)[0]
        ),
      },
    ];
  } else if (getType(step.inputParameters[0]) === ParameterType.Numeric) {
    let value = state?.defaultValue?.rawParameterValues.at(0);
    if (value !== undefined && value.value !== undefined) {
      return [value];
    }
  } else {
    return undefined;
  }
}

function updateStoredUserInputForParameter(
  input: ParameterValue,
  displayValue: string | undefined,
  state: WizardState
) {
  let stepWithParameter = getStepStateContainingParameter(input.id, state);
  if (stepWithParameter !== undefined) {
    let storedParameterInput =
      stepWithParameter.value?.rawParameterValues?.find(
        (storedInput) => storedInput.id === input.id
      );

    if (storedParameterInput !== undefined) {
      storedParameterInput.value = input.value;

      if (displayValue !== undefined) {
        stepWithParameter.value!.display = displayValue;
      }

      storeState(state);
    } else {
      console.error(
        "Tried to overwrite value of wizard parameter for which a value has not yet been stored."
      );
    }
  }
}

function getStepStateContainingParameter(
  id: string,
  state: WizardState
): WizardStepState | undefined {
  return state.steps.find(
    (step) =>
      step.defaultValue?.rawParameterValues.find(
        (parameter) => parameter.id === id
      ) !== undefined ||
      step.value?.rawParameterValues.find(
        (parameter) => parameter.id === id
      ) !== undefined
  );
}

function isDependency(stepId: string, steps: ApiWizardStep[]) {
  return steps.flatMap((step) => step.dependencies).includes(stepId);
}

function hasValue(value: string | undefined) {
  return value !== undefined && value !== null;
}

function useWizard(customer: string, economicProfile: string) {
  const { addError, deleteError } = useAPIError();

  const [wizardSteps, setWizardSteps] = useState<WizardStep[]>([]);
  const [currentStepId, setCurrentStepId] = useState<string>();
  const wizardIsCompleted = useMemo(
    () =>
      wizardSteps.length > 0 && wizardSteps.every((step) => step.isCompleted),
    [wizardSteps]
  );

  const fetchWizardStepsDelayed = useMemo(
    () =>
      debounce((callback: (steps: ApiWizardStep[]) => void) => {
        let state = loadOrCreateState(customer, economicProfile);
        let input = state.steps
          .filter((step) => step.value !== undefined)
          .flatMap((step) => step.value!.rawParameterValues);
        if (input.length === 0) {
          getWizardSteps(customer, economicProfile)
            .then(callback)
            .then(deleteError)
            .catch(() => addError("Wizard step fetch error"));
        } else {
          getWizardStepsByInput(customer, economicProfile, input)
            .then(callback)
            .then(deleteError)
            .catch(() => addError("Wizard step fetch error"));
        }
      }, 600),
    [addError, customer, deleteError, economicProfile]
  );

  if (wizardSteps.length === 0) {
    let state = loadOrCreateState(customer, economicProfile);
    fetchWizardStepsDelayed((apiSteps) => {
      setWizardSteps(mergeApiStepsWithState(apiSteps, state, false));
    });
  }

  const setStepValue = (stepId: string, values: ParameterValue[]) => {
    if (values.length > 0) {
      let state = loadOrCreateState(customer, economicProfile);
      let stepState = getOrCreateStepState(state, stepId);

      let displayValue = getWizardStepDisplayValue(
        values,
        wizardSteps.find((step) => step.id === stepId)?.inputParameters ?? []
      );

      stepState.value = {
        rawParameterValues: values,
        display: displayValue ?? "",
      };
      stepState.completed = true;

      storeState(state);
      if (isDependency(stepId, wizardSteps)) {
        fetchWizardStepsDelayed((apiSteps) => {
          storeDefaultValues(apiSteps, state);
          setWizardSteps(mergeApiStepsWithState(apiSteps, state, true));
        });
      } else {
        setWizardSteps(mergeApiStepsWithState(wizardSteps, state, false));
      }
    }
  };
  const updateParameterValue = (
    value: ParameterValue,
    displayValue: string | undefined
  ) => {
    let state = loadOrCreateState(customer, economicProfile);
    let stepState = getStepStateContainingParameter(value.id, state);

    if (stepState !== undefined && stepState.completed) {
      updateStoredUserInputForParameter(value, displayValue, state);

      if (isDependency(stepState.id, wizardSteps)) {
        fetchWizardStepsDelayed((apiSteps) => {
          storeDefaultValues(apiSteps, state);
          setWizardSteps(mergeApiStepsWithState(apiSteps, state, true));
        });
      } else {
        setWizardSteps(mergeApiStepsWithState(wizardSteps, state, false));
      }
    }
  };

  const isWizardParameter = (parameterId: string) => {
    let state = loadOrCreateState(customer, economicProfile);
    let stepState = getStepStateContainingParameter(parameterId, state);
    return stepState !== undefined;
  };

  const userInputDeviatesFromDefaults = () => {
    return loadOrCreateState(customer, economicProfile).steps.some(
      (stepState) =>
        stepState.defaultValue !== undefined &&
        stepState.defaultValue.rawParameterValues.length > 0 &&
        stepState.value !== undefined &&
        (stepState.value.rawParameterValues.length !==
          stepState.defaultValue.rawParameterValues.length ||
          stepState.value.rawParameterValues.some(
            (storedValue) =>
              storedValue.value !==
              stepState.defaultValue?.rawParameterValues.find(
                (defaultValue) => defaultValue.id
              )?.value
          ))
    );
  };

  const resetToDefaults = () => {
    let state = loadOrCreateState(customer, economicProfile);
    if (state.steps.length > 0 && state.steps.some((step) => step.completed)) {
      for (let step of state.steps) {
        if (step.completed && step.defaultValue !== undefined) {
          step.value = {
            rawParameterValues: [...step.defaultValue.rawParameterValues],
            display: step.defaultValue.display,
          };
        }
      }
      storeState(state);
    }

    setWizardSteps(mergeApiStepsWithState(wizardSteps, state, false));
  };

  return {
    steps: wizardSteps,
    setStepValue,
    updateParameterValue,
    isWizardParameter,
    currentStepId,
    setCurrentStepId,
    wizardIsCompleted,
    userInputDeviatesFromDefaults,
    resetToDefaults,
  };
}
